import {
    downloadTraceDocument,
    getBatchTrace,
    type TraceLink,
} from '@api/cm-sites';
import { MOVEMENT_LABEL, reasonText } from '@components/containers/vendorHistory/ledgerRows';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import StatusPill from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Loader, Stack, Text } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useState } from 'react';

import styles from './TraceView.module.css';

const LINK_COLUMNS: DataSheetColumn<TraceLink>[] = [
    {
        key: 'batchCode',
        header: 'Batch',
        width: 180,
        sticky: true,
        render: (value: string | null) => value ?? '(no batch)',
    },
    {
        key: 'itemId',
        header: 'Item',
        width: 220,
        render: (value: string, row) => row.itemName ?? value,
        valueAccessor: (row) => row.itemName ?? row.itemId,
        csvValue: (value: string, row) => row.itemName ?? value,
    },
    { key: 'quantity', header: 'Quantity', width: 110, align: 'right' },
];

interface TraceViewProps {
    batchId: string;
    /** A manufacturer sees only what passed through its own site; the server enforces it. */
    asSite: boolean;
    onTraceBatch: (batchId: string) => void;
}

/**
 * One batch, where it came from and where it went.
 *
 * A DRILL-DOWN, NOT A REPORT. Every linked batch is itself traceable in one click, because a
 * recall is a walk: you start at the carton somebody complained about, reach the drum it came
 * from, and then need every other run that drum fed. Making that a new search each time is how
 * an investigation loses its place.
 *
 * THE BALANCE IS THE FIRST THING ON SCREEN, and it is loud when it fails. If the movements and
 * the stock disagree, that gap matters more than any of the links below it — it means either a
 * movement nobody recorded or stock nobody can find, and both change what the person does next.
 *
 * THE BOUNDARY IS A WALL, NOT A FOOTNOTE. Brand warehouse stock is not batch-tracked, so what
 * sits below it is a list of movements that COULD have included this batch. In a recall,
 * mistaking that for the traced list is the most expensive possible error, so it is bordered,
 * labelled, and never merged into the tables above it.
 */
const TraceView: FC<TraceViewProps> = ({ batchId, asSite, onTraceBatch }) => {
    const { showToastError } = useToaster();
    const [isDownloading, setIsDownloading] = useState(false);

    const { data: trace, isLoading } = useQuery(
        ['batch-trace', batchId, asSite],
        () => getBatchTrace(batchId, asSite),
        { enabled: !!batchId },
    );

    if (isLoading) {
        return (
            <Group gap={8}>
                <Loader size="xs" />
                <Text fz={13} c={neutralColors.textMuted}>
                    Tracing…
                </Text>
            </Group>
        );
    }

    if (!trace) return null;

    const { batch, balance } = trace;
    const isProduct = batch.itemType?.toLowerCase() === 'product';

    /**
     * The same two figures the Batches panel shows, under the same names, so the strip here
     * and the row that opened it never read as different numbers. "Dispatched" also carries
     * the odd write-off or return; those are itemised in their own table below.
     */
    const figureLabels = isProduct
        ? { in: 'Manufactured', out: 'Dispatched' }
        : { in: 'Received', out: 'Used' };

    /** How much of THIS batch a run made (finished goods) or took (material). */
    const ownShare = (links: TraceLink[]) =>
        links
            .filter((link) => link.batchId === batch.batchId)
            .reduce((sum, link) => sum + link.quantity, 0);

    const runDate = (at: string) =>
        new Date(at).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    /** One row per run: when, how much of this batch, who, against what, and what came out. */
    const runRows = trace.runs.map((run) => ({
        key: run.productionEntryId,
        at: runDate(run.at),
        quantity: isProduct ? ownShare(run.produced) : ownShare(run.consumed),
        order: run.productionOrderCode ?? run.workOrderCode ?? 'To stock',
        made: run.produced.map((link) => link.batchCode ?? '(no batch)').join(', '),
    }));

    // Only worth a column once there is more than one run to tell apart.
    const multiRun = trace.runs.length > 1;

    /** Finished batch: every material line of every run, flat, with the run it belongs to. */
    const consumedRows = trace.runs.flatMap((run) =>
        run.consumed.map((link, index) => ({
            ...link,
            key: `${run.productionEntryId}-${index}`,
            runAt: runDate(run.at),
        })),
    );

    /**
     * Material batch: one row per FINISHED BATCH it went into, summed across runs - the
     * recall set, with how much of this drum is in each. A run that declared more than one
     * finished batch at once carries its full share onto each row, because the ledger does
     * not record how the drum split between them; the Runs column makes that visible.
     */
    const usedInRows = [
        ...trace.runs
            .reduce((acc, run) => {
                const used = ownShare(run.consumed);
                for (const link of run.produced) {
                    const row = acc.get(link.batchId) ?? {
                        ...link,
                        manufactured: 0,
                        used: 0,
                        runs: 0,
                    };
                    row.manufactured += link.quantity;
                    row.used += used;
                    row.runs += 1;
                    acc.set(link.batchId, row);
                }
                return acc;
            }, new Map<string, TraceLink & { manufactured: number; used: number; runs: number }>())
            .values(),
    ];

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            await downloadTraceDocument(batch.batchId, batch.code);
        } catch (err: any) {
            showToastError({
                title: 'Could not produce the document',
                subtitle: err?.message ?? 'Try again, or export the tables as CSV.',
            });
        } finally {
            setIsDownloading(false);
        }
    };

    const linkColumns: DataSheetColumn<TraceLink>[] = [
        ...LINK_COLUMNS,
        {
            key: '_trace',
            header: '',
            width: 100,
            csvValue: () => '',
            render: (_value, row) =>
                row.batchId ? (
                    <Button
                        size="xs"
                        variant={ButtonVariant.SUBTLE}
                        onClick={() => onTraceBatch(row.batchId)}
                    >
                        Trace
                    </Button>
                ) : null,
        },
    ];

    return (
        <Stack gap={20}>
            <div className={styles.subject}>
                <Group justify="space-between">
                    <div>
                        <Group gap={8}>
                            <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                                {batch.code}
                            </Text>
                            {batch.qcStatus && (
                                <StatusPill
                                    label={batch.qcStatus}
                                    tone={batch.qcStatus === 'PASSED' ? 'positive' : 'warning'}
                                />
                            )}
                        </Group>
                        <Text fz={12} c={neutralColors.textSecondary}>
                            {batch.itemName ?? batch.itemId} · made{' '}
                            {batch.manufacturingDate ?? '—'} · expires{' '}
                            {batch.expiryDate ?? '—'}
                        </Text>
                    </div>

                    {!asSite && (
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            onClick={handleDownload}
                            loading={isDownloading}
                        >
                            Download document
                        </Button>
                    )}
                </Group>
            </div>

            <DataSheet
                data={[{ key: 'balance', ...balance }]}
                columns={[
                    { key: 'in', header: figureLabels.in, width: 150, align: 'right' },
                    { key: 'out', header: figureLabels.out, width: 150, align: 'right' },
                    { key: 'onHand', header: 'On hand', width: 150, align: 'right' },
                    {
                        key: 'unaccounted',
                        header: 'Variance',
                        width: 150,
                        align: 'right',
                        // Loud when it is not zero, because a trace that does not add up IS
                        // the finding.
                        render: (value: number, row) => (
                            <Text
                                fz={13}
                                fw={row.balances ? 400 : 600}
                                c={
                                    row.balances
                                        ? neutralColors.textPrimary
                                        : semanticColors.critical.primary
                                }
                            >
                                {value}
                            </Text>
                        ),
                    },
                ]}
                title="Balance"
                getRowKey={(row) => row.key}
                maxHeight="auto"
            />
            <Text
                fz={13}
                c={balance.balances ? neutralColors.textSecondary : semanticColors.critical.primary}
            >
                {balance.balances
                    ? `Variance is ${figureLabels.in.toLowerCase()} minus ${figureLabels.out.toLowerCase()} minus on hand. Zero: every unit of this batch is accounted for.`
                    : `Variance is ${figureLabels.in.toLowerCase()} minus ${figureLabels.out.toLowerCase()} minus on hand. It is not zero: what was recorded moving does not agree with what the system holds - either a movement nobody recorded or stock nobody can find.`}
            </Text>

            {/*
              * ONE TABLE OF RUNS, ONE OF THE OTHER SIDE. A card per run multiplied the page
              * by the number of runs; a drum that fed four runs showed eight tables. Runs are
              * rows, and what each took (or made) is one flat table with the run named on it.
              */}
            {trace.runs.length > 0 && (
                <DataSheet
                    data={runRows}
                    columns={[
                        { key: 'at', header: 'Date', width: 190, sticky: true },
                        {
                            key: 'quantity',
                            header: isProduct ? 'Manufactured' : 'Used',
                            width: 130,
                            align: 'right',
                        },
                        { key: 'order', header: 'Order', width: 180 },
                        // The finished batch a run of this material became; on a finished
                        // batch that is the batch itself, so the column would only repeat.
                        ...(isProduct ? [] : [{ key: 'made', header: 'FG batch', width: 180 }]),
                    ]}
                    title={trace.runs.length === 1 ? 'Production run' : 'Production runs'}
                    getRowKey={(row) => row.key}
                    enableDownload
                    downloadFilename={`trace-${batch.code}-runs.csv`}
                    maxHeight="auto"
                />
            )}

            {isProduct && consumedRows.length > 0 && (
                <DataSheet
                    data={consumedRows}
                    columns={[
                        ...(multiRun
                            ? [{ key: 'runAt', header: 'Run', width: 190, sticky: true }]
                            : []),
                        ...linkColumns,
                    ]}
                    title="RM/PM consumed"
                    getRowKey={(row) => row.key}
                    enableDownload
                    downloadFilename={`trace-${batch.code}-inputs.csv`}
                    maxHeight="auto"
                />
            )}

            {!isProduct && usedInRows.length > 0 && (
                <DataSheet
                    data={usedInRows}
                    columns={[
                        {
                            key: 'batchCode',
                            header: 'FG batch',
                            width: 180,
                            sticky: true,
                            render: (value: string | null) => value ?? '(no batch)',
                        },
                        {
                            key: 'itemId',
                            header: 'Finished goods',
                            width: 220,
                            render: (value: string, row) => row.itemName ?? value,
                            valueAccessor: (row) => row.itemName ?? row.itemId,
                            csvValue: (value: string, row) => row.itemName ?? value,
                        },
                        { key: 'manufactured', header: 'Manufactured', width: 130, align: 'right' },
                        { key: 'used', header: 'RM used', width: 110, align: 'right' },
                        { key: 'runs', header: 'Runs', width: 80, align: 'right' },
                        {
                            key: '_trace',
                            header: '',
                            width: 100,
                            csvValue: () => '',
                            render: (_value, row) => (
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.SUBTLE}
                                    onClick={() => onTraceBatch(row.batchId)}
                                >
                                    Trace
                                </Button>
                            ),
                        },
                    ]}
                    title="Used in manufacturing"
                    // The recall set, named as such: if this drum is bad, these are the batches.
                    subtitle={
                        `${usedInRows.length} finished batch${usedInRows.length === 1 ? '' : 'es'} ` +
                        'drew on this one - the set to recall if it turns out bad'
                    }
                    getRowKey={(row) => `out-${row.batchId}`}
                    enableDownload
                    downloadFilename={`trace-${batch.code}-used-in.csv`}
                    maxHeight="auto"
                />
            )}

            {/*
              * SAID OUT LOUD, not left as a missing table. A finished batch with no run is the
              * common case on day one - most stock was counted in, not declared - and a blank
              * where the runs should be reads as the feature being broken.
              */}
            {trace.runs.length === 0 && isProduct && (
                <div className={styles.hop}>
                    <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                        No run was recorded for this batch here
                    </Text>
                    <Text fz={12} c={neutralColors.textSecondary}>
                        It was counted in or received rather than declared, so there is nothing
                        to say about what went into it. Runs declared from now on will appear
                        here.
                    </Text>
                </div>
            )}

            {trace.shipments.length > 0 && (
                <DataSheet
                    data={trace.shipments.map((shipment, index) => ({ key: index, ...shipment }))}
                    columns={[
                        { key: 'date', header: 'Sent', width: 120, sticky: true },
                        {
                            key: 'quantity',
                            header: 'Quantity',
                            width: 110,
                            align: 'right',
                            // Signed, so the column adds up to what actually left.
                            render: (value: number, row) =>
                                row.isReversal ? `-${value}` : value,
                            valueAccessor: (row) =>
                                row.isReversal ? -row.quantity : row.quantity,
                            csvValue: (value: number, row) =>
                                row.isReversal ? -value : value,
                        },
                        {
                            key: 'purchaseOrderId',
                            header: 'Shipment',
                            width: 180,
                            render: (value: string | null) => value ?? '—',
                        },
                        {
                            key: 'isReversal',
                            header: 'Status',
                            width: 130,
                            render: (value: boolean) =>
                                value ? (
                                    <StatusPill label="Cancelled" tone="warning" />
                                ) : (
                                    <StatusPill label="Sent" tone="positive" />
                                ),
                            csvValue: (value: boolean) => (value ? 'Cancelled' : 'Sent'),
                        },
                        {
                            key: 'warehouseId',
                            header: 'Destination',
                            width: 200,
                            render: (value: string | null) =>
                                value ?? (
                                    <Text fz={13} c={neutralColors.textMuted}>
                                        not stated
                                    </Text>
                                ),
                        },
                    ]}
                    title="Shipments"
                    subtitle="A cancelled row is a dispatch that was recorded and then undone"
                    getRowKey={(row) => String(row.key)}
                    enableDownload
                    maxHeight="auto"
                />
            )}

            {trace.otherExits.length > 0 && (
                <DataSheet
                    data={trace.otherExits.map((exit, index) => ({ key: index, ...exit }))}
                    columns={[
                        { key: 'date', header: 'Date', width: 120, sticky: true },
                        {
                            key: 'movementType',
                            header: 'What',
                            width: 160,
                            render: (value: string) => MOVEMENT_LABEL[value] ?? value,
                        },
                        { key: 'quantity', header: 'Quantity', width: 110, align: 'right' },
                        {
                            key: 'reasonCode',
                            header: 'Reason',
                            width: 200,
                            render: (_value: string | null, row) => reasonText(row) ?? '—',
                        },
                    ]}
                    title="Write-offs and returns"
                    subtitle="Wasted, returned or kept bad, rather than consumed or shipped"
                    getRowKey={(row) => String(row.key)}
                    enableDownload
                    maxHeight="auto"
                />
            )}

            <div className={styles.boundary}>
                <Text fz={13} fw={600} c={semanticColors.critical.primary}>
                    After the brand warehouse (not batch-tracked)
                </Text>
                <Text fz={12} c={neutralColors.textSecondary}>
                    {trace.downstream.boundary}
                </Text>

                {trace.downstream.candidates.length > 0 ? (
                    <DataSheet
                        data={trace.downstream.candidates.map((candidate, index) => ({
                            key: index,
                            ...candidate,
                        }))}
                        columns={[
                            { key: 'date', header: 'Date', width: 120, sticky: true },
                            { key: 'warehouseId', header: 'Warehouse', width: 200 },
                            { key: 'productId', header: 'Item', width: 180 },
                            { key: 'quantity', header: 'Quantity', width: 110, align: 'right' },
                        ]}
                        title="Movements to investigate"
                        subtitle="These happened after it arrived and could have included it"
                        getRowKey={(row) => String(row.key)}
                        enableDownload
                        downloadFilename={`trace-${batch.code}-possible-exposure.csv`}
                        maxHeight="auto"
                    />
                ) : (
                    <Text fz={12} c={neutralColors.textMuted}>
                        No movements of this item were recorded at the receiving warehouse
                        afterwards.
                    </Text>
                )}
            </div>
        </Stack>
    );
};

export default TraceView;
